import React, { useState, useMemo } from 'react';
import { NavArrowLeft } from 'iconoir-react';
import { C, St, FONT_BODY, CARD_STYLE, SPRING } from '../theme';
import { FolderIcon } from '../components/Folder';
import { buildColorMap, sessionFitsScheda, autoAssociaEsercizi } from '../utils';
import { saveDB } from '../database';
import { SchedaDetailSheet } from '../components/Session';

export function SchedePage({ db, setDB, onBack }) {
  const [selectedScheda, setSelectedScheda] = useState(null);
  const [creatingScheda, setCreatingScheda] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'grid' | 'list'
  const [schedaMenu, setSchedaMenu] = useState(null);

  const colorMap = useMemo(() => buildColorMap(db.schede || [], db.sessions), [db.schede, db.sessions]);

  const suggestedSchede = useMemo(() => {
    const names = [...new Set(db.sessions.map(s => s.workoutName).filter(Boolean))];
    return names
      .filter(n => !(db.schede || []).some(sc => sessionFitsScheda(n, sc.name)))
      .map(n => ({ name: n, count: db.sessions.filter(s => s.workoutName === n).length }))
      .sort((a, b) => b.count - a.count);
  }, [db.schede, db.sessions]);

  const handleSaveScheda = u => { setDB(u); saveDB(u); setSelectedScheda(null); setCreatingScheda(false); };

  const toggleSchedaActive = (id) => {
    const newDb = { ...db, schede: db.schede.map(s => s.id === id ? { ...s, active: s.active === false ? true : false } : s) };
    setDB(newDb);
    saveDB(newDb);
    setSchedaMenu(null);
  };

  const saveSuggestedScheda = (name) => {
    const exercises = autoAssociaEsercizi(name, db.sessions);
    const newScheda = { id: Date.now(), name, exercises };
    const newDb = { ...db, schede: [...(db.schede || []), newScheda] };
    setDB(newDb);
    saveDB(newDb);
  };

  const schede = db.schede || [];

  return (
    <div style={St.wrap}>
      {/* Header */}
      <div style={{ paddingTop: 'max(56px, calc(env(safe-area-inset-top, 0px) + 14px))', paddingRight: 20, paddingBottom: 8, paddingLeft: 20, background: C.bg }}>
        {onBack && (
          <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: C.blue, padding: '0 0 10px', fontFamily: FONT_BODY, fontSize: 17, fontWeight: 400 }}>
            <NavArrowLeft width={16} height={16} strokeWidth={2} />
            Indietro
          </button>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h1 style={{ margin: 0, fontFamily: FONT_BODY, fontWeight: 800, fontSize: 34, color: C.text, letterSpacing: -1.2, lineHeight: 1.05 }}>Schede</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* View toggle */}
            <div style={{ display: 'flex', background: C.fill, borderRadius: 9, padding: 2 }}>
              {[{ v: 'grid', icon: (
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                  <rect x="1" y="1" width="6" height="6" rx="1.5" fill="currentColor"/>
                  <rect x="9" y="1" width="6" height="6" rx="1.5" fill="currentColor"/>
                  <rect x="1" y="9" width="6" height="6" rx="1.5" fill="currentColor"/>
                  <rect x="9" y="9" width="6" height="6" rx="1.5" fill="currentColor"/>
                </svg>
              )}, { v: 'list', icon: (
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                  <rect x="1" y="2.5" width="14" height="2" rx="1" fill="currentColor"/>
                  <rect x="1" y="7" width="14" height="2" rx="1" fill="currentColor"/>
                  <rect x="1" y="11.5" width="14" height="2" rx="1" fill="currentColor"/>
                </svg>
              )}].map(o => (
                <button key={o.v} onClick={() => setViewMode(o.v)} style={{
                  width: 32, height: 28, borderRadius: 7, border: 'none', cursor: 'pointer',
                  background: viewMode === o.v ? C.card : 'transparent',
                  color: viewMode === o.v ? C.text : C.subtle,
                  boxShadow: viewMode === o.v ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s',
                }}>
                  {o.icon}
                </button>
              ))}
            </div>
            {/* Add button */}
            <button onClick={() => setCreatingScheda(true)} className="gp-btn-press" style={{ background: C.blue, border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: `transform 0.18s ${SPRING}` }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 2v12M2 8h12" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div style={{ padding: '12px 16px 80px' }}>
        <style>{`@keyframes gpSlideUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}`}</style>

        {/* Grid view */}
        {viewMode === 'grid' && (
          <>
            {schede.length > 0 ? (
              <div style={{ ...CARD_STYLE, padding: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 88px)', gap: 16, justifyContent: 'start' }}>
                {schede.map((s, idx) => {
                  const inactive = s.active === false;
                  const col = inactive ? C.subtle : (colorMap[s.name] || C.blue);
                  const cnt = db.sessions.filter(sess => sessionFitsScheda(sess.workoutName, s.name)).length;
                  let pressTimer = null;
                  const startPress = () => { pressTimer = setTimeout(() => setSchedaMenu({ id: s.id, active: s.active }), 500); };
                  const cancelPress = () => { clearTimeout(pressTimer); };
                  return (
                    <div key={s.id} style={{ opacity: inactive ? 0.45 : 1, position: 'relative', animation: `gpSlideUp 0.45s ${idx * 0.045}s ${SPRING} both` }}
                      onContextMenu={e => { e.preventDefault(); setSchedaMenu({ id: s.id, active: s.active }); }}
                      onMouseDown={startPress} onMouseUp={cancelPress} onMouseLeave={cancelPress}
                      onTouchStart={startPress} onTouchEnd={cancelPress} onTouchCancel={cancelPress}>
                      <FolderIcon
                        accentColor={col}
                        label={s.name}
                        sublabel={inactive ? 'Inattiva' : (cnt > 0 ? `${cnt} sess.` : null)}
                        onClick={() => setSelectedScheda(s)}
                        size={88}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0', color: C.subtle, fontSize: 14 }}>
                Nessuna scheda. Premi + per crearne una.
              </div>
            )}
          </>
        )}

        {/* List view */}
        {viewMode === 'list' && (
          <>
            {schede.length > 0 ? (
              <div style={{ ...CARD_STYLE, overflow: 'hidden', padding: 0, background: '#fff' }}>
                {schede.map((s, idx) => {
                  const inactive = s.active === false;
                  const col = inactive ? C.subtle : (colorMap[s.name] || C.blue);
                  const cnt = db.sessions.filter(sess => sessionFitsScheda(sess.workoutName, s.name)).length;
                  const exCount = (s.exercises || []).length;
                  return (
                    <div key={s.id} style={{ opacity: inactive ? 0.5 : 1, animation: `gpSlideUp 0.45s ${idx * 0.045}s ${SPRING} both` }}>
                      <div
                        onClick={() => setSelectedScheda(s)}
                        style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}>
                        <FolderIcon accentColor={col} size={44} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, fontFamily: FONT_BODY, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{s.name}</div>
                          <div style={{ fontSize: 12, color: C.subtle, fontFamily: FONT_BODY, marginTop: 1 }}>
                            {exCount > 0 ? `${exCount} esercizi` : 'Nessun esercizio'}{cnt > 0 ? ` · ${cnt} sessioni` : ''}
                            {inactive ? ' · Inattiva' : ''}
                          </div>
                        </div>
                        <svg width="7" height="12" viewBox="0 0 7 12" fill="none"><path d="M1 1l5 5-5 5" stroke="rgba(60,60,67,0.30)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </div>
                      {idx < schede.length - 1 && <div style={{ height: '0.5px', background: 'rgba(60,60,67,0.08)', marginLeft: 74 }}/>}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0', color: C.subtle, fontSize: 14 }}>
                Nessuna scheda. Premi + per crearne una.
              </div>
            )}
          </>
        )}

        {/* Schede suggerite dalla cronologia */}
        {suggestedSchede.length > 0 && (
          <div style={{ marginTop: schede.length > 0 ? 24 : 4 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.subtle, letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 8 }}>
              Rilevate dalla cronologia
            </div>
            <div style={{ ...CARD_STYLE, overflow: 'hidden', padding: 0 }}>
              {suggestedSchede.map((s, i) => (
                <div key={s.name}>
                  <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px', gap: 12, WebkitTapHighlightColor: 'transparent' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{s.name}</div>
                      <div style={{ fontSize: 12, color: C.subtle, marginTop: 1 }}>{s.count} sessioni in cronologia</div>
                    </div>
                    <button
                      onClick={() => saveSuggestedScheda(s.name)}
                      style={{ background: C.blue, border: 'none', borderRadius: 6, color: '#fff', fontSize: 13, fontWeight: 600, padding: '6px 16px', cursor: 'pointer', fontFamily: FONT_BODY, flexShrink: 0 }}>
                      Salva
                    </button>
                  </div>
                  {i < suggestedSchede.length - 1 && <div style={{ height: 1, background: 'rgba(60,60,67,0.10)', margin: '0 16px' }}/>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sheet scheda */}
      {(selectedScheda || creatingScheda) && (
        <SchedaDetailSheet
          scheda={creatingScheda ? null : selectedScheda}
          db={db}
          onSave={handleSaveScheda}
          onClose={() => { setSelectedScheda(null); setCreatingScheda(false); }}
        />
      )}

      {/* Action menu */}
      {schedaMenu && (() => {
        const s = schede.find(x => x.id === schedaMenu.id);
        const inactive = s?.active === false;
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.30)' }}
            onClick={() => setSchedaMenu(null)}>
            <div style={{
              position: 'fixed', bottom: 0, left: 0, right: 0,
              background: '#fff', borderRadius: '16px 16px 0 0',
              paddingBottom: 'max(24px, env(safe-area-inset-bottom, 16px))',
              maxWidth: 560, margin: '0 auto',
            }} onClick={e => e.stopPropagation()}>
              <div style={{ width: 36, height: 4, borderRadius: 2, background: 'rgba(120,120,128,0.28)', margin: '12px auto 16px' }}/>
              <div style={{ padding: '0 16px 8px', fontSize: 15, fontWeight: 600, color: C.text, fontFamily: FONT_BODY, textAlign: 'center' }}>{s?.name}</div>
              <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button
                  onClick={() => { setSelectedScheda(s); setSchedaMenu(null); }}
                  style={{ width: '100%', padding: '14px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: FONT_BODY, fontSize: 15, fontWeight: 600, textAlign: 'left', background: 'rgba(0,122,255,0.08)', color: C.blue }}>
                  Modifica scheda
                </button>
                <button
                  onClick={() => toggleSchedaActive(schedaMenu.id)}
                  style={{ width: '100%', padding: '14px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: FONT_BODY, fontSize: 15, fontWeight: 600, textAlign: 'left', background: inactive ? 'rgba(52,199,89,0.08)' : 'rgba(255,59,48,0.08)', color: inactive ? C.green : C.red }}>
                  {inactive ? 'Attiva scheda' : 'Disattiva scheda'}
                </button>
                <button
                  onClick={() => setSchedaMenu(null)}
                  style={{ width: '100%', padding: '14px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: FONT_BODY, fontSize: 15, fontWeight: 500, textAlign: 'center', background: 'rgba(120,120,128,0.10)', color: C.text }}>
                  Annulla
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
